import React, { useRef, useEffect, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, Link, Minus } from 'lucide-react';

function ToolBtn({ onClick, children, title }) {
  return (
    <button type="button" onMouseDown={e => { e.preventDefault(); onClick(); }} title={title}
      className="p-1.5 rounded hover:bg-[#E2DFD8] transition-colors text-[#475569] hover:text-[#1B3A6B]">
      {children}
    </button>
  );
}

export default function RichEmailEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Only update innerHTML from external changes (template selection)
  useEffect(() => {
    if (!isInternalChange.current && editorRef.current) {
      const currentHTML = editorRef.current.innerHTML;
      if (value !== currentHTML) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const exec = useCallback((cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border border-[#E2DFD8] rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-[#F9F8F6] border-b border-[#E2DFD8]">
        <select onChange={e => { exec('formatBlock', e.target.value); e.target.value = ''; }}
          className="text-xs px-1.5 py-1 bg-transparent text-[#475569] border-none cursor-pointer" defaultValue="">
          <option value="" disabled>Format</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="p">Normal</option>
        </select>
        <div className="w-px h-5 bg-[#E2DFD8] mx-1" />
        <ToolBtn onClick={() => exec('bold')} title="Bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Underline"><Underline className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-[#E2DFD8] mx-1" />
        <ToolBtn onClick={() => exec('foreColor', '#1B3A6B')} title="Navy"><div className="w-3.5 h-3.5 rounded-full bg-[#1B3A6B]" /></ToolBtn>
        <ToolBtn onClick={() => exec('foreColor', '#B8860B')} title="Gold"><div className="w-3.5 h-3.5 rounded-full bg-[#B8860B]" /></ToolBtn>
        <ToolBtn onClick={() => exec('foreColor', '#0F1A2A')} title="Dark"><div className="w-3.5 h-3.5 rounded-full bg-[#0F1A2A]" /></ToolBtn>
        <div className="w-px h-5 bg-[#E2DFD8] mx-1" />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullets"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbers"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-[#E2DFD8] mx-1" />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Left"><AlignLeft className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Center"><AlignCenter className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={insertLink} title="Link"><Link className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertHorizontalRule')} title="Divider"><Minus className="w-3.5 h-3.5" /></ToolBtn>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        data-testid="rich-editor"
        data-placeholder={placeholder || "Compose your email..."}
        className="min-h-[160px] max-h-[300px] overflow-y-auto px-4 py-3 text-sm text-[#0F1A2A] focus:outline-none"
        style={{ fontFamily: 'Georgia, serif', lineHeight: 1.7 }}
        suppressContentEditableWarning
      />
      <style>{`
        [data-testid="rich-editor"]:empty::before {
          content: attr(data-placeholder);
          color: #94A3B8;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
